import React, { useState, useEffect } from 'react';
import { ThinkingStep } from '../types';

interface DamiaReflexionProps {
    steps: ThinkingStep[];
    isActive: boolean;
    initialCollapsed?: boolean;
}

const DamiaReflexion: React.FC<DamiaReflexionProps> = ({ steps, isActive, initialCollapsed = false }) => {
    const [isExpanded, setIsExpanded] = useState(!initialCollapsed);

    // Auto-collapse quand la réflexion est terminée
    useEffect(() => {
        if (!isActive && steps.length > 0) {
            const timer = setTimeout(() => setIsExpanded(false), 1500);
            return () => clearTimeout(timer);
        }
        if (isActive) {
            setIsExpanded(true);
        }
    }, [isActive, steps.length]);

    if (steps.length === 0) return null;

    return (
        <div className="flex w-full mb-4 justify-start">
            <div className="bg-mcdm-primary-200 dark:bg-mcdm-third-300 rounded-lg overflow-hidden max-w-lg w-full border border-gray-200 dark:border-gray-600">
                {/* Header déroulable */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center justify-between w-full px-4 py-3 text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-mcdm-primary-300/30 dark:hover:bg-mcdm-third-200/30 transition-colors duration-200"
                >
                    <div className="flex items-center space-x-2">
                        {isActive && (
                            <div className="w-2 h-2 bg-mcdm-secondary-400 rounded-full animate-pulse" />
                        )}
                        {!isActive && steps.length > 0 && (
                            <svg className="w-4 h-4 text-mcdm-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                        <span className="font-medium">
                            {isActive ? 'Réflexion...' : 'Réflexion terminée'}
                        </span>
                    </div>
                    <svg
                        className={`w-4 h-4 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Liste des étapes */}
                <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                    <div className="px-4 pb-3 space-y-2">
                        {steps.map((step, index) => (
                            <div
                                key={step.stepId}
                                className="flex items-start space-x-3"
                                style={{
                                    animation: `fadeSlideIn 0.3s ease-out ${index * 0.05}s both`,
                                }}
                            >
                                {/* Icône de statut */}
                                <div className="mt-0.5 flex-shrink-0">
                                    {step.status === 'pending' && (
                                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                                    )}
                                    {step.status === 'in_progress' && (
                                        <div className="w-4 h-4 rounded-full border-2 border-mcdm-secondary-400 border-t-transparent animate-spin" />
                                    )}
                                    {step.status === 'completed' && (
                                        <svg className="w-4 h-4 text-mcdm-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                    {step.status === 'failed' && (
                                        <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    )}
                                </div>

                                {/* Contenu de l'étape */}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs ${
                                        step.status === 'in_progress'
                                            ? 'text-mcdm-third-500 dark:text-mcdm-primary-100 font-medium'
                                            : step.status === 'completed'
                                            ? 'text-mcdm-third-400 dark:text-mcdm-primary-200'
                                            : step.status === 'failed'
                                            ? 'text-red-500 dark:text-red-400'
                                            : 'text-gray-400 dark:text-gray-500'
                                    }`}>
                                        {step.label}
                                    </p>
                                    {step.status === 'completed' && step.data?.summary && (
                                        <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-400 mt-0.5 truncate">
                                            {step.data.summary}
                                        </p>
                                    )}
                                    {step.status === 'completed' && step.data?.domains && step.data.domains.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {step.data.domains.slice(0, 3).map((domain, i) => (
                                                <span
                                                    key={i}
                                                    className="inline-block text-xs px-1.5 py-0.5 bg-mcdm-blue-100/20 dark:bg-mcdm-blue-100/10 text-mcdm-blue-100 rounded"
                                                >
                                                    {domain}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-4px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default DamiaReflexion;
